"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search } from "lucide-react";
import ErrorState from "./ErrorState";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { SecuredFooter } from "./ui/secured-footer";
import { getWalletAddresses } from "../services/digitalWalletService";
import { getPayAddresses } from "../services/settingsService";
import { useToast } from "../hooks/use-toast";
import { Skeleton } from "./ui/skeleton";
import AddWallet from "./AddWallet";
import AddPayID from "./AddPayID";
import AddressContact from "./AddressContact";
import AddBeneficiaryPayID from "./AddBeneficiaryPayID";
import EditBeneficiaryPayID from "./EditBeneficiaryPayID";
import ContactListItem from "./ContactListItem";
import noAddressImg from "../assets/images/settings/no_results.svg";
import noWalletImg from "../assets/images/purchase.png";

interface WalletAddress {
  _id: string;
  firstName?: string;
  lastName?: string;
  addressName?: string;
  walletAddress?: string;
  assetName?: string;
  assetIcon?: string;
  chainName?: string;
  networkId?: string;
  assetId?: string;
}

interface PayAddress {
  _id: string;
  name?: string;
  lastname?: string;
  payId?: string;
  payIdType?: "payid" | "email" | "phone";
  countryCode?: string;
  countryDialCode?: string;
}

type AddressTab = "wallet" | "88pay";
type ViewState =
  | "list"
  | "add-wallet"
  | "add-payid"
  | "add-beneficiary-payid"
  | "edit-beneficiary-payid"
  | "view-contact";

interface AddressBookPageProps {
  onClose?: () => void;
}

// Detect PayID type from the payId value
const detectPayIdType = (
  payId?: string
): "payid" | "email" | "phone" | undefined => {
  if (!payId) return undefined;

  // Email detection
  if (payId.includes("@")) {
    return "email";
  }

  // Phone detection (starts with + or contains mostly digits)
  const digitsOnly = payId.replace(/[\s\-()]/g, "");
  if (digitsOnly.startsWith("+") || /^\d{7,}$/.test(digitsOnly)) {
    return "phone";
  }

  // Default to payid
  return "payid";
};

export default function AddressBookPage({ onClose }: AddressBookPageProps) {
  const [activeTab, setActiveTab] = useState<AddressTab>("wallet");
  const [viewState, setViewState] = useState<ViewState>("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [walletAddresses, setWalletAddresses] = useState<WalletAddress[]>([]);
  const [payAddresses, setPayAddresses] = useState<PayAddress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<
    WalletAddress | PayAddress | null
  >(null);
  const [action, setAction] = useState<"create" | "update">("create");
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const { toast } = useToast();
  const isFetching = useRef(false);

  const fetchAddresses = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;

    try {
      setIsLoading(true);
      setLoadError(null);
      const [walletResponse, payResponse] = await Promise.all([
        getWalletAddresses(),
        getPayAddresses(),
      ]);

      if (walletResponse?.data?.data?.address) {
        setWalletAddresses(walletResponse.data.data.address);
      }

      if (payResponse?.data?.data?.addresses) {
        setPayAddresses(payResponse.data.data.addresses);
      }
    } catch (error) {
      setLoadError("Failed to load contacts");
      toast({
        description: "Failed to load contacts",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, [toast]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  useEffect(() => {
    if (shouldRefresh) {
      isFetching.current = false;
      fetchAddresses();
      setShouldRefresh(false);
    }
  }, [shouldRefresh, fetchAddresses]);

  const handleBackToList = () => {
    setViewState("list");
    setSelectedContact(null);
  };

  const filteredWalletAddresses = walletAddresses.filter(
    (wallet) =>
      wallet.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wallet.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wallet.addressName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wallet.walletAddress?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPayAddresses = payAddresses.filter(
    (pay) =>
      pay.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pay.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pay.payId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInitials = (firstName?: string, lastName?: string): string => {
    const first = firstName?.charAt(0)?.toUpperCase() || "";
    const last = lastName?.charAt(0)?.toUpperCase() || "";
    return first + last || "?";
  };

  const handleContactClick = (
    contact: WalletAddress | PayAddress,
    type: "wallet" | "pay"
  ) => {
    setSelectedContact(contact);
    setViewState("view-contact");
  };

  const handleAddNew = () => {
    setSelectedContact(null);
    setAction("create");
    if (activeTab === "wallet") {
      setViewState("add-wallet");
    } else {
      setViewState("add-beneficiary-payid");
    }
  };

  // Render Add Wallet view
  if (viewState === "add-wallet") {
    return (
      <AddWallet
        setPageNo={(page: number) => {
          if (page === 0) handleBackToList();
        }}
        setRefresh={setShouldRefresh}
        type="Crypto"
        action={action}
        updateUser={selectedContact as WalletAddress | null}
      />
    );
  }

  // Render Add PayID view
  if (viewState === "add-payid") {
    return (
      <AddPayID
        setPageNo={(page: number) => {
          if (page === 0) handleBackToList();
        }}
        PayCount={payAddresses.length}
        action={action}
        updateUser={selectedContact as PayAddress | null}
        setRefresh={setShouldRefresh}
      />
    );
  }

  // Render Add Beneficiary PayID view (new multi-step flow)
  if (viewState === "add-beneficiary-payid") {
    return (
      <AddBeneficiaryPayID
        onBack={handleBackToList}
        onSuccess={() => {
          setShouldRefresh(true);
          handleBackToList();
        }}
      />
    );
  }

  // Render Edit Beneficiary PayID view
  if (viewState === "edit-beneficiary-payid" && selectedContact) {
    return (
      <EditBeneficiaryPayID
        contact={selectedContact as PayAddress}
        onBack={() => {
          setViewState("view-contact");
        }}
        onSuccess={() => {
          setShouldRefresh(true);
          handleBackToList();
        }}
        onDelete={() => {
          setShouldRefresh(true);
          handleBackToList();
        }}
      />
    );
  }

  // Render View Contact view
  if (viewState === "view-contact" && selectedContact) {
    return (
      <AddressContact
        setPageNo={(page: number) => {
          if (page === 0) handleBackToList();
          if (page === 1) {
            setAction("update");
            setViewState("add-wallet");
          }
          if (page === 2) {
            setAction("update");
            setViewState("edit-beneficiary-payid");
          }
          if (page === 3) {
            setViewState("add-beneficiary-payid");
          }
        }}
        updateUser={selectedContact}
        setUpdateUser={setSelectedContact}
        setAction={setAction}
        type={activeTab === "wallet" ? "Crypto" : "Pay"}
        setRefresh={setShouldRefresh}
      />
    );
  }

  // Render main list view
  return (
    <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-hidden">
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-8 pb-0">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-[20px] font-semibold text-text-secondary">
              Address book
            </h2>
            <div className="relative w-[358px]">
              <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-text-secondary w-3 h-3" />
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-[rgba(235,233,239,0.3)] rounded-full outline-none focus:ring-2 focus:ring-brand-primary text-[14px] text-text-secondary placeholder:text-text-secondary"
              />
            </div>
          </div>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as AddressTab)}
            className="w-full"
          >
            <TabsList className="w-full h-[64px] p-2 bg-transparent border border-[#EBE9EF] rounded-full">
              <TabsTrigger
                value="wallet"
                className="flex-1 h-[48px] rounded-full lg:text-lg text-[14px] font-semibold data-[state=active]:bg-[#33125F] data-[state=active]:text-white data-[state=inactive]:text-[#6D6584]"
              >
                Wallet Address
              </TabsTrigger>
              <TabsTrigger
                value="88pay"
                className="flex-1 h-[48px] rounded-full lg:text-lg text-[14px] font-semibold data-[state=active]:bg-[#33125F] data-[state=active]:text-white data-[state=inactive]:text-[#6D6584]"
              >
                88Pay
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Content */}
        <div className="px-8 pt-8">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton
                  key={i}
                  className="w-full h-[88px] rounded-2xl bg-gray-100"
                />
              ))}
            </div>
          ) : loadError ? (
            <ErrorState
              title="Failed to load contacts"
              message="Please check your connection and try again"
              onTryAgain={() => {
                isFetching.current = false;
                fetchAddresses();
              }}
              withCard={false}
              withFooter={false}
            />
          ) : (
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-2">
              {activeTab === "wallet" ? (
                filteredWalletAddresses.length > 0 ? (
                  filteredWalletAddresses.map((wallet) => (
                    <ContactListItem
                      key={wallet._id}
                      name={`${wallet.firstName || ""} ${wallet.lastName || ""}`.trim()}
                      initials={getInitials(wallet.firstName, wallet.lastName)}
                      onClick={() => handleContactClick(wallet, "wallet")}
                      assetIcons={
                        wallet.assetIcon
                          ? [
                              {
                                src: wallet.assetIcon,
                                alt: wallet.assetName || "",
                              },
                            ]
                          : undefined
                      }
                    />
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-16">
                    <img src={searchTerm ? noAddressImg : noWalletImg} alt="" />
                    <p className="text-[16px] text-text-secondary font-semibold mt-5">
                      {searchTerm
                        ? "We couldn't find that contact"
                        : "You have not added an address"}
                    </p>
                    {!searchTerm && (
                      <p className="text-[16px] text-text-secondary font-semibold mt-2">
                        Add a new address below
                      </p>
                    )}
                  </div>
                )
              ) : filteredPayAddresses.length > 0 ? (
                filteredPayAddresses.map((pay) => (
                  <ContactListItem
                    key={pay._id}
                    name={`${pay.name || ""} ${pay.lastname || ""}`.trim()}
                    initials={getInitials(pay.name, pay.lastname)}
                    onClick={() => handleContactClick(pay, "pay")}
                    payIdType={pay.payIdType || detectPayIdType(pay.payId)}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <img src={searchTerm ? noAddressImg : noWalletImg} alt="" />
                  <p className="text-[16px] text-text-secondary font-semibold mt-5">
                    {searchTerm
                      ? "We couldn't find that contact"
                      : "You have not added a Pay ID"}
                  </p>
                  {!searchTerm && (
                    <p className="text-[16px] text-text-secondary font-semibold mt-2">
                      Add a new Pay ID below
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Add Button - hide during loading or error */}
          {!isLoading && !loadError && (
            <div className="flex justify-center py-4 mt-4 border-t border-[#EBE9EF]">
              <Button
                onClick={handleAddNew}
                className="h-[48px] px-12 rounded-full text-[14px] font-semibold text-white"
                style={{
                  background:
                    "linear-gradient(69.77deg, #57249C 10.66%, #EB3366 113.47%)",
                }}
              >
                {activeTab === "wallet"
                  ? "Add new contact"
                  : "Add New Beneficiary"}
              </Button>
            </div>
          )}
        </div>

        <SecuredFooter />
      </CardContent>
    </Card>
  );
}
